import { useCustom } from '@refinedev/core';
import { Table, Tag, Button, Select } from 'antd';
import { RotateCcw } from 'lucide-react';
import { useState, type FC } from 'react';

import RefundModal from '@/components/ops/billing/RefundModal';

interface RefundRecord {
  id: string;
  organizationId: string;
  organizationName: string;
  amount: number;
  currency: string;
  reason: string;
  status: 'pending' | 'completed' | 'failed';
  createdAt: string;
}

const STATUS_TAG: Record<string, { color: string; text: string }> = {
  pending: { color: 'processing', text: 'En attente' },
  completed: { color: 'success', text: 'Effectue' },
  failed: { color: 'error', text: 'Echoue' },
};

const fmtDate = (d: string | null) =>
  d ? new Date(d).toLocaleDateString('fr-FR') : '--';

const fmtAmount = (amount: number, currency: string) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(amount / 100);

const RefundsPage: FC = () => {
  const [refundOpen, setRefundOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | undefined>();

  const query: Record<string, string> = {};
  if (statusFilter) query.status = statusFilter;

  const { data, isLoading, refetch } = useCustom<{ data: { items: RefundRecord[]; total: number } }>({
    url: '/api/v1/admin/billing/refunds',
    method: 'get',
    config: { query },
  });

  const refunds = data?.data?.data?.items ?? [];

  return (
    <div className="space-y-6">
      <GradientHeader />

      <div className="flex flex-wrap items-center gap-3">
        <Select
          allowClear
          placeholder="Statut"
          className="w-40"
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: 'pending', label: 'En attente' },
            { value: 'completed', label: 'Effectue' },
            { value: 'failed', label: 'Echoue' },
          ]}
        />
        <div className="ml-auto">
          <Button
            type="primary"
            icon={<RotateCcw className="h-3.5 w-3.5" />}
            onClick={() => setRefundOpen(true)}
          >
            Rembourser
          </Button>
        </div>
      </div>

      <div className="fc-card overflow-hidden p-0">
        <Table
          dataSource={refunds}
          loading={isLoading}
          rowKey="id"
          size="middle"
          scroll={{ x: 800 }}
          pagination={{ pageSize: 15 }}
        >
          <Table.Column
            dataIndex="id"
            title="ID"
            width={100}
            render={(v: string) => (
              <span className="font-mono text-xs text-slate-600 dark:text-slate-400">
                {v?.slice(0, 8)}...
              </span>
            )}
          />
          <Table.Column
            dataIndex="organizationName"
            title="Organisation"
            render={(name: string, r: RefundRecord) => (
              <span className="text-sm text-slate-900 dark:text-white">
                {name || r.organizationId?.slice(0, 8)}
              </span>
            )}
          />
          <Table.Column
            dataIndex="amount"
            title="Montant"
            width={120}
            render={(amount: number, r: RefundRecord) => (
              <span className="font-medium text-slate-900 dark:text-white">
                {fmtAmount(amount, r.currency)}
              </span>
            )}
          />
          <Table.Column dataIndex="currency" title="Devise" width={80} />
          <Table.Column
            dataIndex="reason"
            title="Raison"
            ellipsis
            render={(reason: string) => (
              <span className="text-sm text-slate-600 dark:text-slate-300">
                {reason || '--'}
              </span>
            )}
          />
          <Table.Column
            dataIndex="status"
            title="Statut"
            width={120}
            render={(s: string) => {
              const cfg = STATUS_TAG[s] ?? { color: 'default', text: s };
              return <Tag color={cfg.color}>{cfg.text}</Tag>;
            }}
          />
          <Table.Column
            dataIndex="createdAt"
            title="Date"
            width={110}
            render={(d: string) => (
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {fmtDate(d)}
              </span>
            )}
          />
        </Table>
      </div>

      <RefundModal
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        onSuccess={() => {
          setRefundOpen(false);
          void refetch();
        }}
      />
    </div>
  );
};

const GradientHeader: FC = () => (
  <div className="fc-gradient-header rounded-2xl p-6 text-white">
    <div className="flex items-center gap-3">
      <RotateCcw className="h-6 w-6" />
      <div>
        <h1 className="text-xl font-bold">Remboursements</h1>
        <p className="mt-1 text-sm text-blue-100">
          Suivi et gestion des remboursements Stripe
        </p>
      </div>
    </div>
  </div>
);

export default RefundsPage;
